import { useState, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

// ============================================================
// Icons
// ============================================================
const IIcon = ({ size = 14, stroke = 1.75, className = "", children }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size}
    viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round"
    className={className} aria-hidden="true">{children}</svg>
);
const ISearch = (p) => (<IIcon {...p}><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></IIcon>);
const IColumns = (p) => (<IIcon {...p}><rect x="3" y="3" width="18" height="18" rx="2" /><line x1="9" y1="3" x2="9" y2="21" /><line x1="15" y1="3" x2="15" y2="21" /></IIcon>);
const IPlus = (p) => (<IIcon {...p}><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></IIcon>);
const IChevDown = (p) => (<IIcon {...p}><polyline points="6 9 12 15 18 9" /></IIcon>);
const ICheck = (p) => (<IIcon {...p}><polyline points="20 6 9 17 4 12" /></IIcon>);
const IX = (p) => (<IIcon {...p}><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></IIcon>);
const IDownload = (p) => (<IIcon {...p}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></IIcon>);
const IExternal = (p) => (<IIcon {...p}><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></IIcon>);
const IFilter = (p) => (<IIcon {...p}><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></IIcon>);
const IDots = (p) => (<IIcon {...p}><circle cx="12" cy="12" r="1" /><circle cx="19" cy="12" r="1" /><circle cx="5" cy="12" r="1" /></IIcon>);
const IPin = (p) => (<IIcon {...p}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></IIcon>);
const IMap = (p) => (<IIcon {...p}><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" /><line x1="8" y1="2" x2="8" y2="18" /><line x1="16" y1="6" x2="16" y2="22" /></IIcon>);
const IGavel = (p) => (<IIcon {...p}><path d="m14 13-7.5 7.5a2.12 2.12 0 0 1-3-3L11 10" /><path d="m16 16 6-6" /><path d="m8 8 6-6" /><path d="m9 7 8 8" /><path d="m21 11-8-8" /></IIcon>);
const IReport = (p) => (<IIcon {...p}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" /></IIcon>);

// ============================================================
// Column definitions
// ============================================================
// align: 'left' | 'right' | 'center'
// width: numeric px width
// group: section in column picker
const COLUMNS = [
  // Anagrafica
  { key: "comune",       label: "Comune",        group: "Anagrafica",     width: 140, align: "left",  sticky: true,  default: true },
  { key: "indirizzo",    label: "Indirizzo",     group: "Anagrafica",     width: 240, align: "left",  sticky: true,  default: true },
  { key: "tipologia",    label: "Tipologia",     group: "Anagrafica",     width: 130, align: "left",  default: true },
  { key: "piano",        label: "Piano",         group: "Anagrafica",     width: 80,  align: "left",  default: true },
  { key: "composizione", label: "Composizione",  group: "Anagrafica",     width: 200, align: "left",  default: false },
  { key: "vani",         label: "Vani",          group: "Anagrafica",     width: 60,  align: "right", default: false },
  { key: "camere",       label: "Camere",        group: "Anagrafica",     width: 80,  align: "right", default: false },
  { key: "bagni",        label: "Bagni",         group: "Anagrafica",     width: 70,  align: "right", default: false },
  { key: "box",          label: "Box",           group: "Anagrafica",     width: 60,  align: "center", default: false },
  { key: "cantina",      label: "Cantina",       group: "Anagrafica",     width: 80,  align: "center", default: false },
  { key: "terrazzo",     label: "Terrazzo",      group: "Anagrafica",     width: 80,  align: "center", default: false },
  { key: "giardino",     label: "Giardino",      group: "Anagrafica",     width: 80,  align: "center", default: false },

  // Metrature
  { key: "mq",           label: "Mq",            group: "Metrature",      width: 70,  align: "right", default: true,  unit: "mq" },
  { key: "mqComm",       label: "Mq comm.",      group: "Metrature",      width: 90,  align: "right", default: false, unit: "mq" },
  { key: "mqCalp",       label: "Mq calp.",      group: "Metrature",      width: 90,  align: "right", default: false, unit: "mq" },

  // Asta
  { key: "baseAsta",     label: "Base asta",     group: "Asta",           width: 130, align: "right", default: true,  type: "euro" },
  { key: "offertaMin",   label: "Offerta min.",  group: "Asta",           width: 130, align: "right", default: false, type: "euro" },
  { key: "valMercato",   label: "Val. mercato",  group: "Asta",           width: 130, align: "right", default: false, type: "euro" },
  { key: "eurMqMercato", label: "€/mq mercato",  group: "Asta",           width: 110, align: "right", default: false, type: "euro" },
  { key: "eurMqAsta",    label: "€/mq asta",     group: "Asta",           width: 110, align: "right", default: false, type: "euro" },
  { key: "gap",          label: "Gap %",         group: "Asta",           width: 90,  align: "right", default: true,  type: "pct" },
  { key: "dataAsta",     label: "Data asta",     group: "Asta",           width: 110, align: "left",  default: true },
  { key: "scadOfferte",  label: "Scad. offerte", group: "Asta",           width: 120, align: "left",  default: false },
  { key: "modalita",     label: "Modalità",      group: "Asta",           width: 110, align: "left",  default: false },
  { key: "tent",         label: "Tent.",         group: "Asta",           width: 70,  align: "right", default: false },

  // Economiche
  { key: "liquidita",    label: "Liquidità",     group: "Economiche",     width: 110, align: "right", default: false, type: "euro" },
  { key: "ristrutt",     label: "Ristrutt.",     group: "Economiche",     width: 110, align: "right", default: false, type: "euro" },
  { key: "ristPrezzo",   label: "Rist/Prezzo",   group: "Economiche",     width: 110, align: "right", default: false, type: "pct" },

  // Stato / Operativo
  { key: "disponibilita",label: "Disponibilità", group: "Stato",          width: 130, align: "left",  default: true },
  { key: "analisi",      label: "Analisi",       group: "Stato",          width: 110, align: "left",  default: false },
  { key: "lead",         label: "Lead",          group: "Stato",          width: 70,  align: "right", default: true },
  { key: "corrisp",      label: "Corrisp.",      group: "Stato",          width: 90,  align: "right", default: false },

  // Procedura
  { key: "tribunale",    label: "Tribunale",     group: "Procedura",      width: 160, align: "left",  default: true },
  { key: "procedura",    label: "Procedura",     group: "Procedura",      width: 130, align: "left",  default: false },
  { key: "pvp",          label: "PVP",           group: "Procedura",      width: 80,  align: "center", default: false },
];

const COLUMN_GROUPS = ["Anagrafica", "Metrature", "Asta", "Economiche", "Stato", "Procedura"];

// ============================================================
// Mock data — 18 immobili across Italian tribunals
// ============================================================

// Extra imports for routing + API
import { Link, useSearchParams } from 'react-router-dom';
import { fetchImmobili, fetchStats, fetchFilterOptions, togglePreferito, toggleScarta, startAstemaster, astemasterStatus, publishGetrix } from '../lib/api';
import FilterPanel, { countActiveFilters } from '../components/FilterPanel';
import OmiReportModal from '../components/OmiReportModal';


// ============================================================
// Formatters & helpers
// ============================================================
const fmtEuro0 = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);
const fmtNum = (n) => new Intl.NumberFormat("it-IT").format(n);
const fmtPct = (n) => `${n > 0 ? "+" : ""}${n.toFixed(1)}%`;

function YesBadge({ on }) {
  return on
    ? <span className="im-yes" title="Presente"><ICheck size={11} stroke={2.4} /></span>
    : <span className="im-no" aria-label="Assente">—</span>;
}

function PctCell({ v }) {
  const tone = v <= -25 ? "good" : v <= -10 ? "ok" : v > 0 ? "bad" : "neutral";
  return <span className={`im-pct im-pct--${tone}`}>{fmtPct(v)}</span>;
}

function DisponibilitaCell({ v }) {
  const t = v === "Libero" ? "libero" : v === "Occupato" ? "occupato" : "altro";
  return <span className={`im-dot-tag im-dot-tag--${t}`}><i></i>{v}</span>;
}

function AnalisiCell({ v }) {
  const t = v === "Completa" ? "ok" : v === "In corso" ? "mid" : "todo";
  return <span className={`im-tag im-tag--${t}`}>{v}</span>;
}

function TipologiaCell({ v }) {
  const map = {
    "A/1": "Signorile", "A/2": "Civile", "A/3": "Economica", "A/4": "Popolare",
    "A/7": "Villino", "A/8": "Villa", "A/10": "Uffici", "C/2": "Magazzino", "D/7": "Industriale",
  };
  return (
    <span className="im-tipo">
      <span className="im-tipo__code">{v}</span>
      <span className="im-tipo__lbl">{map[v] || ""}</span>
    </span>
  );
}

function ComuneCell({ v }) {
  return (
    <span className="im-comune">
      <IPin size={11} />
      <span>{v}</span>
    </span>
  );
}

function CorrispCell({ v }) {
  const tone = v >= 80 ? "good" : v >= 65 ? "ok" : "low";
  return (
    <span className={`im-score im-score--${tone}`}>
      <span className="im-score__bar"><i style={{ width: `${v}%` }}></i></span>
      <span className="im-score__num">{v}</span>
    </span>
  );
}

function TruncatedText({ text, max = 35, className = "" }) {
  const [showTip, setShowTip] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const elRef = useRef(null);
  const tipRef = useRef(null);

  const needsTruncation = typeof text === 'string' && text.length > max;

  const showAt = () => {
    if (!elRef.current) return;
    const r = elRef.current.getBoundingClientRect();
    setPos({ top: r.bottom + 4, left: r.left });
    setShowTip(true);
  };

  useEffect(() => {
    if (!showTip) return;
    const handleClick = (e) => {
      if (
        tipRef.current && !tipRef.current.contains(e.target) &&
        elRef.current && !elRef.current.contains(e.target)
      ) setShowTip(false);
    };
    const handleKey = (e) => { if (e.key === 'Escape') setShowTip(false); };
    const handleScroll = () => setShowTip(false);
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    document.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [showTip]);

  if (!needsTruncation) return <span className={className}>{text}</span>;

  const truncated = text.slice(0, max).trimEnd() + '…';
  return (
    <>
      <span
        ref={elRef}
        className={`${className} im-truncated`}
        onMouseEnter={showAt}
        onMouseLeave={() => setShowTip(false)}
        onClick={(e) => { e.stopPropagation(); showTip ? setShowTip(false) : showAt(); }}
      >
        {truncated}
      </span>
      {showTip && createPortal(
        <div
          ref={tipRef}
          className="im-tip"
          style={{ top: pos.top, left: pos.left }}
          role="tooltip"
        >
          {text}
        </div>,
        document.body
      )}
    </>
  );
}

function cellContent(col, row) {
  const v = row[col.key];
  if (v == null || v === "") return <span className="im-na">—</span>;

  switch (col.key) {
    case "comune":        return <Link to={`/app/immobili/${row.id}`} className="im-link" style={{ textDecoration: 'none', color: 'inherit' }}><ComuneCell v={v} /></Link>;
    case "lead":          return <Link to={`/app/immobili/${row.id}`} className="im-link"><strong>{v}</strong></Link>;
    case "tipologia":     return <TipologiaCell v={v} />;
    case "gap":
    case "ristPrezzo":    return <PctCell v={v} />;
    case "disponibilita": return <DisponibilitaCell v={v} />;
    case "analisi":       return <AnalisiCell v={v} />;
    case "corrisp":       return <CorrispCell v={v} />;
    case "box":
    case "cantina":
    case "terrazzo":
    case "giardino":      return <YesBadge on={!!v} />;
    case "pvp":
      return <a href="#" className="im-link" onClick={(e) => e.preventDefault()}>
        <IExternal size={11} /><span>PVP</span></a>;
    case "indirizzo":     return <TruncatedText text={v} max={35} className="im-strong" />;
    case "tribunale":     return <span className="im-tribunale">{v}</span>;
    case "procedura":     return <span className="im-mono">{v}</span>;
    case "tent":          return <span className="im-tent">{v}°</span>;
    default:
      if (col.type === "euro") return fmtEuro0(v);
      if (col.type === "pct") return fmtPct(v);
      if (col.unit === "mq") return <span><span className="im-num">{fmtNum(v)}</span> <span className="im-unit">mq</span></span>;
      if (typeof v === "number") return fmtNum(v);
      return v;
  }
}

// ============================================================
// Column picker popover
// ============================================================
function ColumnPicker({ visible, setVisible, onClose }) {
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const toggle = (key) => {
    const next = new Set(visible);
    if (next.has(key)) next.delete(key); else next.add(key);
    setVisible(next);
  };

  const groupCols = (g) => COLUMNS.filter((c) => c.group === g);
  const allInGroup = (g) => groupCols(g).every((c) => visible.has(c.key));
  const someInGroup = (g) => groupCols(g).some((c) => visible.has(c.key));

  const toggleGroup = (g) => {
    const next = new Set(visible);
    const all = allInGroup(g);
    groupCols(g).forEach((c) => {
      if (c.sticky) { next.add(c.key); return; } // sticky always visible
      if (all) next.delete(c.key); else next.add(c.key);
    });
    setVisible(next);
  };

  const reset = () => {
    setVisible(new Set(COLUMNS.filter((c) => c.default).map((c) => c.key)));
  };
  const showAll = () => {
    setVisible(new Set(COLUMNS.map((c) => c.key)));
  };

  return (
    <div className="im-pop" ref={ref} role="dialog" aria-label="Mostra / nascondi colonne">
      <div className="im-pop__head">
        <div>
          <div className="im-pop__title">Colonne</div>
          <div className="im-pop__sub">{visible.size} di {COLUMNS.length} visibili</div>
        </div>
        <div className="im-pop__head-actions">
          <button className="im-pop__action" onClick={reset}>Predefinite</button>
          <button className="im-pop__action" onClick={showAll}>Mostra tutto</button>
        </div>
      </div>
      <div className="im-pop__body">
        {COLUMN_GROUPS.map((g) => {
          const cols = groupCols(g);
          return (
            <div key={g} className="im-pop__group">
              <button
                className="im-pop__group-head"
                onClick={() => toggleGroup(g)}
                aria-pressed={allInGroup(g)}
              >
                <span className={`im-pop__gcheck ${allInGroup(g) ? "is-all" : someInGroup(g) ? "is-some" : ""}`}>
                  {allInGroup(g) ? <ICheck size={10} stroke={3} /> : someInGroup(g) ? <span className="im-pop__gcheck-dash"></span> : null}
                </span>
                <span>{g}</span>
                <span className="im-pop__group-count">{cols.filter((c) => visible.has(c.key)).length}/{cols.length}</span>
              </button>
              <div className="im-pop__opts">
                {cols.map((c) => (
                  <label key={c.key} className="im-pop__opt" data-on={visible.has(c.key)} data-locked={!!c.sticky}>
                    <input
                      type="checkbox"
                      checked={visible.has(c.key)}
                      disabled={c.sticky}
                      onChange={() => toggle(c.key)}
                    />
                    <span className="im-pop__check">
                      {visible.has(c.key) && <ICheck size={10} stroke={3} />}
                    </span>
                    <span className="im-pop__opt-lbl">{c.label}</span>
                    {c.sticky && <span className="im-pop__opt-pin">fissa</span>}
                  </label>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================
// Immobili Page
// ============================================================
const STORAGE_KEY = "rivelasta:immobili:cols";

function loadVisible() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const arr = JSON.parse(raw);
      if (Array.isArray(arr)) return new Set(arr);
    }
  } catch {}
  return new Set(COLUMNS.filter((c) => c.default).map((c) => c.key));
}

function saveVisible(set) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(set))); } catch {}
}

// ============================================================
// (le viste valutazione/preferiti/scartate/tutti sono ora dentro FilterPanel)

const VERDETTO_FILTERS = [
  { id: '',             label: 'Tutti i verdetti' },
  { id: 'eccezionale',  label: '⭐ Eccezionale' },
  { id: 'consigliata',  label: '✅ Consigliata' },
  { id: 'con_riserva',  label: '⚠️ Con riserva' },
  { id: 'da_evitare',   label: '🚫 Da evitare' },
  { id: 'analizzate',   label: 'Già analizzate' },
  { id: 'da_analizzare',label: 'Da analizzare' },
];

const CATEGORIA_FILTERS = [
  { id: '',             label: 'Tutte le categorie' },
  { id: 'residenziale', label: '🏠 Residenziale' },
  { id: 'commerciale',  label: '🏬 Commerciale' },
];

// ============================================================
// API row -> table row mapping
// ============================================================
function fmtDateIt(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return null; }
}
function fmtDateTimeIt(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return null; }
}

function apiRowToTable(r) {
  return {
    id: r.id,
    _raw: r,
    comune: r.comune || '—',
    indirizzo: r.indirizzo || '—',
    tipologia: r.categoria_catastale || r.tipologia || null,
    piano: r.piano,
    composizione: r.composizione,
    vani: null,
    camere: r.n_camere,
    bagni: r.n_bagni,
    box: r.box_auto,
    cantina: r.cantina,
    terrazzo: r.terrazzo_balcone,
    giardino: r.giardino,
    lead: r.lead_count,
    mq: r.superficie_mq,
    mqComm: r.superficie_commerciale,
    mqCalp: r.superficie_calpestabile,
    baseAsta: r.prezzo_base,
    offertaMin: r.offerta_minima,
    valMercato: r.prezzo_mercato_totale,
    eurMqMercato: r.prezzo_mercato_mq,
    eurMqAsta: r.superficie_mq && r.offerta_minima
      ? Math.round(r.offerta_minima / r.superficie_mq)
      : null,
    gap: r.gap_percentuale,
    dataAsta: fmtDateIt(r.data_vendita),
    scadOfferte: fmtDateTimeIt(r.termine_offerte),
    modalita: r.modalita_vendita,
    tent: r.tentativo_vendita,
    liquidita: null,
    ristrutt: null,
    ristPrezzo: null,
    disponibilita: r.disponibilita,
    analisi: r.url_report ? 'Completa' : (r.stato_analisi || 'Da fare'),
    needs_reanalysis: !!r.needs_reanalysis,
    corrisp: r.n_corrispettivi,
    tribunale: r.tribunale,
    procedura: r.n_procedura && r.anno_procedura ? `Esec. ${r.n_procedura}/${String(r.anno_procedura).slice(-2)}` : null,
    pvp: r.url_pvp,
    url_pvp: r.url_pvp,
    url_report: r.url_report,
    rivelasta_verdetto: r.rivelasta_verdetto,
    scartato: r.scartato,
    preferito: r.preferito,
    job_status: r.job_status,
    lat: r.lat,
    lng: r.lng,
  };
}

// ============================================================
// Row actions kebab menu
// ============================================================
function RowActionsMenu({ row, onTogglePreferito, onToggleScarta, onStartAstemaster, onPublishGetrix, onOpenOmi, publishing }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, right: 0 });
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  const handleToggle = (e) => {
    e.stopPropagation();
    if (open) { setOpen(false); return; }
    const rect = btnRef.current.getBoundingClientRect();
    setPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target) &&
        btnRef.current && !btnRef.current.contains(e.target)
      ) setOpen(false);
    };
    const handleKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    const handleScroll = () => setOpen(false);
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    document.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [open]);

  return (
    <div className="im-actions" onClick={(e) => e.stopPropagation()}>
      <button
        ref={btnRef}
        type="button"
        className="im-act-btn"
        title="Azioni"
        aria-label="Azioni"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={handleToggle}
      >
        <IDots size={13} />
      </button>
      {open && createPortal(
        <div
          ref={menuRef}
          className="im-row-menu"
          style={{ top: pos.top, right: pos.right }}
          role="menu"
          onClick={(e) => e.stopPropagation()}
        >
          {row.url_pvp && (
            <button
              type="button"
              className="im-row-menu__item"
              role="menuitem"
              onClick={() => { window.open(row.url_pvp, '_blank', 'noopener'); setOpen(false); }}
            >
              <IGavel size={14} /> <span>Apri scheda PVP</span>
            </button>
          )}
          {row.url_report && (
            <>
              <button
                type="button"
                className="im-row-menu__item im-row-menu__item--accent"
                role="menuitem"
                onClick={() => { window.open(row.url_report, '_blank', 'noopener'); setOpen(false); }}
              >
                <IReport size={14} /> <span>Apri report Rivelasta</span>
              </button>
              <button
                type="button"
                className="im-row-menu__item"
                role="menuitem"
                onClick={() => { window.open(`/api/immobili/${row.id}/report.pdf`, '_blank', 'noopener'); setOpen(false); }}
              >
                <IReport size={14} /> <span>Scarica PDF</span>
              </button>
              {row.url_pvp && onStartAstemaster && row.job_status !== 'queued' && row.job_status !== 'running' && (
                <button
                  type="button"
                  className={row.needs_reanalysis ? 'im-row-menu__item im-row-menu__item--accent' : 'im-row-menu__item'}
                  role="menuitem"
                  onClick={() => { onStartAstemaster(row, { force: true }); setOpen(false); }}
                  title={row.needs_reanalysis ? "L'annuncio PVP è stato aggiornato dopo l'analisi precedente" : "Rilancia l'analisi con eventuali dati/perizia aggiornati"}
                >
                  <span aria-hidden="true">↻</span>{' '}
                  <span>Ripeti analisi{row.needs_reanalysis ? ' (aggiornata!)' : ''}</span>
                </button>
              )}
            </>
          )}
          {!row.url_report && row.job_status === 'running' && (
            <button
              type="button"
              className="im-row-menu__item"
              role="menuitem"
              disabled
              style={{ opacity: 0.7, cursor: 'default' }}
            >
              <span aria-hidden="true">🟡</span> <span>Analisi in corso…</span>
            </button>
          )}
          {!row.url_report && row.job_status === 'queued' && (
            <button
              type="button"
              className="im-row-menu__item"
              role="menuitem"
              disabled
              style={{ opacity: 0.7, cursor: 'default' }}
            >
              <span aria-hidden="true">🟠</span> <span>Analisi in coda…</span>
            </button>
          )}
          {!row.url_report
            && row.job_status !== 'running'
            && row.job_status !== 'queued'
            && row.url_pvp
            && onStartAstemaster && (
            <button
              type="button"
              className="im-row-menu__item im-row-menu__item--accent"
              role="menuitem"
              onClick={() => { onStartAstemaster(row); setOpen(false); }}
            >
              <span aria-hidden="true">▶</span> <span>Avvia analisi</span>
            </button>
          )}
          {onOpenOmi && row.indirizzo && row.comune && (
            <button
              type="button"
              className="im-row-menu__item"
              role="menuitem"
              onClick={() => { onOpenOmi(row); setOpen(false); }}
              title="Analisi Deep OMI: compravendite dichiarate all'AdE nella zona dell'immobile"
            >
              <span aria-hidden="true">📊</span> <span>Analisi Deep OMI</span>
            </button>
          )}
          {onPublishGetrix && (
            <button
              type="button"
              className="im-row-menu__item im-row-menu__item--accent"
              role="menuitem"
              disabled={publishing}
              onClick={() => { onPublishGetrix(row); setOpen(false); }}
              title={row.getrix_id_annuncio
                ? `Già pubblicato — idAnnuncio ${row.getrix_id_annuncio}. Cliccando si crea una nuova bozza.`
                : (row.getrix_ready
                    ? 'Crea bozza su Getrix (Sezione ASTE)'
                    : 'Verrà arricchito automaticamente prima di pubblicare')}
            >
              <span aria-hidden="true">{publishing ? '⏳' : '⤴'}</span>
              <span>
                {publishing
                  ? 'Pubblico…'
                  : (row.getrix_id_annuncio ? 'Ripubblica su Getrix' : 'Pubblica su Getrix')}
              </span>
            </button>
          )}
          {row.lat && row.lng && (
            <Link
              to={`/app/mappa?focus=${row.id}`}
              className="im-row-menu__item"
              role="menuitem"
              onClick={() => setOpen(false)}
            >
              <IMap size={14} /> <span>Vedi su mappa</span>
            </Link>
          )}
          <button
            type="button"
            className="im-row-menu__item"
            role="menuitem"
            onClick={() => { onTogglePreferito(row); setOpen(false); }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24"
                 fill={row.preferito ? 'currentColor' : 'none'}
                 stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                 style={{ color: row.preferito ? 'oklch(0.65 0.18 25)' : undefined }}>
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
            </svg>
            <span>{row.preferito ? 'Rimuovi dai preferiti' : 'Aggiungi ai preferiti'}</span>
          </button>
          <button
            type="button"
            className="im-row-menu__item"
            role="menuitem"
            onClick={() => { onToggleScarta(row); setOpen(false); }}
          >
            <IX size={14} /> <span>{row.scartato ? 'Rimetti in valutazione' : 'Scarta'}</span>
          </button>
        </div>,
        document.body
      )}
    </div>
  );
}

// ============================================================
// Page
// ============================================================
function Pagination({ current, last, onChange }) {
  const pages = [];
  const window = 1;
  const add = (n) => pages.push(n);
  const addGap = () => { if (pages[pages.length - 1] !== '…') pages.push('…'); };
  for (let i = 1; i <= last; i++) {
    if (i === 1 || i === last || (i >= current - window && i <= current + window)) add(i);
    else addGap();
  }
  return (
    <nav className="mi-pager" aria-label="Paginazione">
      <button
        type="button"
        className="mi-pager__btn"
        onClick={() => onChange(current - 1)}
        disabled={current <= 1}
        aria-label="Pagina precedente"
      >‹</button>
      {pages.map((p, i) => p === '…' ? (
        <span key={`gap-${i}`} className="mi-pager__gap">…</span>
      ) : (
        <button
          key={p}
          type="button"
          className={`mi-pager__btn${p === current ? ' mi-pager__btn--active' : ''}`}
          onClick={() => onChange(p)}
          aria-current={p === current ? 'page' : undefined}
        >{p}</button>
      ))}
      <button
        type="button"
        className="mi-pager__btn"
        onClick={() => onChange(current + 1)}
        disabled={current >= last}
        aria-label="Pagina successiva"
      >›</button>
    </nav>
  );
}

function ImmobiliPage() {
  const [visible, setVisible] = useState(() => loadVisible());
  const [pickerOpen, setPickerOpen] = useState(false);
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [filterOptions, setFilterOptions] = useState({});

  // Filters synced to URL via useSearchParams for shareable links
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get('view') || 'valutazione';
  const verdetto = searchParams.get('verdetto') || '';
  const categoria = searchParams.get('categoria') || '';
  const query = searchParams.get('q') || '';

  const [sort, setSort] = useState({ key: 'dataAsta', dir: 'asc' });

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => { saveVisible(visible); }, [visible]);

  // Map table sort keys to API sort keys (only some are server-side sortable)
  const apiSortKey = (key) => ({
    dataAsta: 'data_vendita',
    offertaMin: 'offerta_minima',
    baseAsta: 'prezzo_base',
    gap: 'gap_percentuale',
    mq: 'superficie_mq',
    comune: 'comune',
  })[key] || 'created_at';

  // Build full param object from URL — includes all advanced filters
  const buildApiParams = () => {
    const params = {
      view,
      per_page: 100,
      page: parseInt(searchParams.get('page') || '1', 10) || 1,
      sort_by: apiSortKey(sort.key),
      sort_dir: sort.dir,
    };
    if (verdetto) params.verdetto = verdetto;
    if (categoria) params.categoria = categoria;
    if (query) params.q = query;
    // Forward all advanced filter keys present in URL
    const passthrough = [
      'comune', 'tribunale', 'piano', 'piano_categoria', 'liquidita_tier',
      'livello_ristrutturazione', 'disponibilita', 'tentativo_vendita',
      'tipologia_categoria', 'tipologia_escludi',
      'piano_escludi', 'piano_categoria_escludi', 'disponibilita_escludi',
      'prezzo_max', 'mq_min', 'mq_max',
      'dentro_gra', 'nuovi', 'scadute', 'needs_reanalysis', 'scadenza_30gg',
      'center_lat', 'center_lng', 'radius_km',
    ];
    passthrough.forEach((k) => {
      const v = searchParams.get(k);
      if (v != null && v !== '' && v !== 'false') params[k] = v;
    });
    return params;
  };

  // Fetch when filters/sort change
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);

    fetchImmobili(buildApiParams())
      .then((res) => {
        if (!alive) return;
        setRows(res.data.map(apiRowToTable));
        setMeta(res.meta || null);
      })
      .catch((e) => {
        if (!alive) return;
        console.error('fetchImmobili failed', e);
        setError(e?.message || 'Errore caricamento immobili');
        setRows([]);
      })
      .finally(() => alive && setLoading(false));

    return () => { alive = false; };
    // Re-fetch on ANY URL param change (covers all filter combinations)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString(), sort.key, sort.dir]);

  // Stats + filter options fetched once
  useEffect(() => {
    fetchStats().then(setStats).catch(() => {});
    fetchFilterOptions().then(setFilterOptions).catch(() => {});
  }, []);

  const activeFilterCount = countActiveFilters(searchParams);

  const orderedCols = useMemo(() => COLUMNS.filter((c) => visible.has(c.key)), [visible]);

  const onSort = (key) => {
    setSort((s) => s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });
    if (searchParams.has('page')) {
      const next = new URLSearchParams(searchParams);
      next.delete('page');
      setSearchParams(next, { replace: true });
    }
  };

  const stickyOffsets = useMemo(() => {
    const offs = {};
    let acc = 36;
    orderedCols.forEach((c) => {
      if (c.sticky) {
        offs[c.key] = acc;
        acc += c.width;
      }
    });
    return offs;
  }, [orderedCols]);

  const updateFilter = (patch) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === '' || v == null) next.delete(k);
      else next.set(k, String(v));
    });
    if (!('page' in patch)) next.delete('page');
    setSearchParams(next, { replace: true });
  };

  const goToPage = (n) => {
    const next = new URLSearchParams(searchParams);
    if (n <= 1) next.delete('page');
    else next.set('page', String(n));
    setSearchParams(next, { replace: true });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleTogglePreferito = async (row) => {
    try {
      const res = await togglePreferito(row.id);
      setRows((rs) => rs.map((r) => r.id === row.id ? { ...r, preferito: res.preferito } : r));
    } catch (e) { console.error(e); }
  };

  const handleToggleScarta = async (row) => {
    try {
      const res = await toggleScarta(row.id);
      // If we're in "valutazione" view and the user just scartato'd a row, remove it from view
      if (view === 'valutazione' && res.scartato) {
        setRows((rs) => rs.filter((r) => r.id !== row.id));
      } else if (view === 'scartate' && !res.scartato) {
        setRows((rs) => rs.filter((r) => r.id !== row.id));
      } else {
        setRows((rs) => rs.map((r) => r.id === row.id ? { ...r, scartato: res.scartato } : r));
      }
    } catch (e) { console.error(e); }
  };

  // Polling dei job astemaster: ogni 5s, per ogni row con job_status in {queued,running}
  // chiama lo status endpoint e aggiorna la riga (job_status, url_report, rivelasta_verdetto).
  // Si auto-cancella se non ci sono più job in corso.
  const pollingTimersRef = useRef({});
  useEffect(() => {
    const inFlightIds = rows
      .filter((r) => r.job_status === 'queued' || r.job_status === 'running')
      .map((r) => r.id);

    // Cancello timer per id non più in volo
    Object.keys(pollingTimersRef.current).forEach((id) => {
      if (!inFlightIds.includes(Number(id))) {
        clearInterval(pollingTimersRef.current[id]);
        delete pollingTimersRef.current[id];
      }
    });

    // Creo timer per id nuovi in volo
    inFlightIds.forEach((id) => {
      if (pollingTimersRef.current[id]) return;
      pollingTimersRef.current[id] = setInterval(async () => {
        try {
          const s = await astemasterStatus(id);
          setRows((rs) => rs.map((r) => r.id === id ? {
            ...r,
            job_status: ['done', 'failed'].includes(s.status) ? null : s.status,
            url_report: s.url_report ?? r.url_report,
            rivelasta_verdetto: s.rivelasta_verdetto ?? r.rivelasta_verdetto,
          } : r));
        } catch (e) { /* network blip: riprovo al tick successivo */ }
      }, 5000);
    });

    return () => {
      // unmount: cancello tutti i timer
      Object.values(pollingTimersRef.current).forEach(clearInterval);
      pollingTimersRef.current = {};
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows.map((r) => `${r.id}:${r.job_status}`).join('|')]);

  const handleStartAstemaster = async (row, { force = false } = {}) => {
    if (!row.url_pvp) return;
    if (force) {
      const ok = window.confirm(
        `Ripetere l'analisi per l'immobile #${row.id} (${row.comune || ''})?\n\n` +
        `Il report precedente verrà sovrascritto quando quello nuovo sarà pronto.`
      );
      if (!ok) return;
    }
    // Optimistic: subito 'queued' + reset url_report se rianalisi
    setRows((rs) => rs.map((r) => r.id === row.id
      ? { ...r, job_status: 'queued', ...(force ? { url_report: null, needs_reanalysis: false, analisi: 'Da fare' } : {}) }
      : r));
    try {
      const res = await startAstemaster(row.id, { force });
      setRows((rs) => rs.map((r) => r.id === row.id
        ? { ...r, job_status: res.status || 'queued' }
        : r));
    } catch (e) {
      console.error(e);
      // Rollback
      setRows((rs) => rs.map((r) => r.id === row.id ? { ...r, job_status: null } : r));
      window.alert("Impossibile avviare l'analisi: " + (e?.response?.data?.message || e?.message || 'errore sconosciuto'));
    }
  };

  const [publishingId, setPublishingId] = useState(null);
  const [omiRow, setOmiRow] = useState(null);
  const handlePublishGetrix = async (row) => {
    const willEnrich = !row.getrix_ready;
    const ok = window.confirm(
      `Pubblicare l'immobile #${row.id} (${row.comune || ''}) come BOZZA su Getrix?\n\n` +
      (willEnrich
        ? `⚠ L'immobile non è ancora stato arricchito. Verrà fatto ora automaticamente (richiede qualche secondo) e poi pubblicato.\n\n`
        : '') +
      `Verrà creato un annuncio nella sezione ASTE dell'organizzazione corrente.`
    );
    if (!ok) return;
    setPublishingId(row.id);
    try {
      const res = await publishGetrix(row.id);
      setRows((rs) => rs.map((r) => r.id === row.id
        ? { ...r, getrix_id_annuncio: res.idAnnuncio, getrix_ready: true }
        : r));
      const goNow = window.confirm(
        `✓ Bozza creata su Getrix.\n` +
        `idAnnuncio: ${res.idAnnuncio}\n` +
        `idAstaImmobile: ${res.idAstaImmobile}\n\n` +
        `Aprire la bozza in una nuova scheda?`
      );
      if (goNow) window.open(res.url, '_blank', 'noopener');
    } catch (e) {
      const code = e?.response?.data?.code;
      const msg  = e?.response?.data?.error || e?.message || 'errore sconosciuto';
      if (code === 'getrix_cookies_missing' || code === 'getrix_session_expired') {
        window.alert(`Sessione Getrix non valida.\n\n${msg}\n\nContatta l'amministratore per rinfrescare i cookies.`);
      } else {
        window.alert('Pubblicazione fallita: ' + msg);
      }
    } finally {
      setPublishingId(null);
    }
  };

  return (
    <main className="content content--inbox">
      <div className="page__header">
        <div className="page__title-wrap">
          <h1 className="page__title">Immobili</h1>
          <p className="page__sub">
            {meta?.total != null
              ? `${meta.total.toLocaleString('it-IT')} lotti in asta seguiti dallo studio`
              : 'Lotti in asta seguiti dallo studio'}
          </p>
        </div>
        <div className="page__actions">
          <Link to="/app/mappa" className="btn btn--ghost" style={{ textDecoration: 'none' }}>
            <IMap size={13} />
            <span>Mappa</span>
          </Link>
        </div>
      </div>

      <div className="im-toolbar">
        <div className="im-toolbar__right" style={{ marginLeft: 'auto' }}>
          <div className="mi-search">
            <ISearch size={14} className="mi-search__icon" />
            <input
              type="text"
              className="mi-search__input"
              placeholder="Cerca comune, indirizzo, tribunale…"
              value={query}
              onChange={(e) => updateFilter({ q: e.target.value })}
            />
          </div>
          <select
            className="btn btn--outline"
            value={categoria}
            onChange={(e) => updateFilter({ categoria: e.target.value })}
            style={{ minWidth: 160 }}
          >
            {CATEGORIA_FILTERS.map((f) => (
              <option key={f.id} value={f.id}>{f.label}</option>
            ))}
          </select>
          <select
            className="btn btn--outline"
            value={verdetto}
            onChange={(e) => updateFilter({ verdetto: e.target.value })}
            style={{ minWidth: 180 }}
          >
            {VERDETTO_FILTERS.map((f) => (
              <option key={f.id} value={f.id}>{f.label}</option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn--outline"
            onClick={() => setFilterPanelOpen(true)}
            aria-haspopup="dialog"
          >
            <IFilter size={13} />
            <span>Filtri</span>
            {activeFilterCount > 0 && <span className="fp__btn-count">{activeFilterCount}</span>}
          </button>
          <div className="im-cols-wrap">
            <button
              className="btn btn--outline"
              data-active={pickerOpen}
              onClick={() => setPickerOpen((o) => !o)}
              aria-expanded={pickerOpen}
              aria-haspopup="dialog"
            >
              <IColumns size={13} />
              <span>Colonne</span>
              <span className="im-cols-count">{visible.size}/{COLUMNS.length}</span>
              <IChevDown size={12} />
            </button>
            {pickerOpen && (
              <ColumnPicker
                visible={visible}
                setVisible={setVisible}
                onClose={() => setPickerOpen(false)}
              />
            )}
          </div>
        </div>
      </div>

      {/* === Card view (mobile only) === */}
      <div className="im-cards im-cards--mobile-only" role="list">
        {loading && (
          <div className="mi-empty"><div className="mi-empty__title">Caricamento…</div></div>
        )}
        {error && !loading && (
          <div className="mi-empty">
            <div className="mi-empty__title">Errore</div>
            <div className="mi-empty__body">{error}</div>
          </div>
        )}
        {!loading && !error && rows.length === 0 && (
          <div className="mi-empty">
            <div className="mi-empty__title">Nessun immobile corrisponde ai filtri</div>
            <div className="mi-empty__body">Prova a cambiare vista, verdetto o pulire la ricerca.</div>
          </div>
        )}
        {!loading && !error && rows.map((row) => (
          <article key={row.id} className="im-card" data-verdetto={row.rivelasta_verdetto || ''} role="listitem">
            <header className="im-card__head">
              <div className="im-card__title">
                <div className="im-card__comune">
                  {row.comune || '—'}
                  {row.preferito && <span className="im-card__pref" title="Preferito">♥</span>}
                </div>
                <div className="im-card__addr">{row.indirizzo}</div>
              </div>
              {row.rivelasta_verdetto && (
                <span className={`im-card__badge im-card__badge--${row.rivelasta_verdetto}`}>
                  {row.rivelasta_verdetto.replace(/_/g, ' ')}
                </span>
              )}
            </header>

            <div className="im-card__chips">
              {row.tipologia && <span className="im-card__chip im-card__chip--tipo">{row.tipologia}</span>}
              {row.piano != null && <span className="im-card__chip">Piano {row.piano}</span>}
              {row.mq != null && <span className="im-card__chip">{fmtNum(row.mq)} mq</span>}
              {row.disponibilita && <span className="im-card__chip im-card__chip--disp">{row.disponibilita}</span>}
            </div>

            <div className="im-card__numbers">
              <div className="im-card__num">
                <div className="im-card__num-lbl">Offerta min.</div>
                <div className="im-card__num-val">{row.offertaMin != null ? fmtEuro0(row.offertaMin) : '—'}</div>
              </div>
              <div className="im-card__num">
                <div className="im-card__num-lbl">Base asta</div>
                <div className="im-card__num-val">{row.baseAsta != null ? fmtEuro0(row.baseAsta) : '—'}</div>
              </div>
              {row.gap != null && (
                <div className="im-card__num">
                  <div className="im-card__num-lbl">Gap mercato</div>
                  <div
                    className="im-card__num-val"
                    style={{
                      color:
                        row.gap <= -25 ? 'oklch(0.55 0.13 150)' :
                        row.gap <= -10 ? 'oklch(0.55 0.12 90)' :
                        row.gap > 0 ? 'oklch(0.55 0.18 25)' : undefined,
                    }}
                  >
                    {fmtPct(row.gap)}
                  </div>
                </div>
              )}
            </div>

            <div className="im-card__meta">
              {row.dataAsta && <span><strong>Asta:</strong> {row.dataAsta}</span>}
              {row.tribunale && <span>{row.tribunale}</span>}
            </div>

            <div className="im-card__actions">
              {row.url_pvp && (
                <a
                  href={row.url_pvp}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="im-card__act"
                  title="Apri scheda PVP"
                >
                  <IGavel size={15} /> <span>PVP</span>
                </a>
              )}
              {row.url_report && (
                <>
                  <a
                    href={row.url_report}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="im-card__act im-card__act--accent"
                    title="Apri report Rivelasta"
                  >
                    <IReport size={15} /> <span>Report</span>
                  </a>
                  <a
                    href={`/api/immobili/${row.id}/report.pdf`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="im-card__act"
                    title="Scarica il report in PDF"
                  >
                    <IReport size={15} /> <span>PDF</span>
                  </a>
                </>
              )}
              {row.lat && row.lng && (
                <Link to={`/app/mappa?focus=${row.id}`} className="im-card__act" title="Mappa">
                  <IMap size={15} /> <span>Mappa</span>
                </Link>
              )}
              <button
                type="button"
                className="im-card__act"
                onClick={() => handleTogglePreferito(row)}
                style={{ color: row.preferito ? 'oklch(0.55 0.18 25)' : undefined }}
                title={row.preferito ? 'Rimuovi dai preferiti' : 'Preferito'}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill={row.preferito ? 'currentColor' : 'none'}
                     stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                </svg>
                <span>{row.preferito ? 'Tolto' : 'Pref'}</span>
              </button>
              <button
                type="button"
                className="im-card__act"
                onClick={() => handleToggleScarta(row)}
                title={row.scartato ? 'Rimetti in valutazione' : 'Scarta'}
              >
                <IX size={15} /> <span>Scarta</span>
              </button>
            </div>
          </article>
        ))}
      </div>

      {/* === Table view (desktop only) === */}
      <div className="im-table-wrap">
        <div className="im-table-scroll">
          <table className="im-table" style={{ minWidth: orderedCols.reduce((s, c) => s + c.width, 36) + 80 }}>
            <thead>
              <tr>
                <th className="im-th im-th--check" style={{ left: 0 }}>
                  <label className="mi-checkbox">
                    <input type="checkbox" aria-label="Seleziona tutti" />
                    <span className="mi-checkbox__box"></span>
                  </label>
                </th>
                {orderedCols.map((c) => {
                  const isSort = sort.key === c.key;
                  const stickyStyle = c.sticky
                    ? { left: stickyOffsets[c.key], width: c.width, minWidth: c.width }
                    : { width: c.width, minWidth: c.width };
                  return (
                    <th
                      key={c.key}
                      className={`im-th im-th--${c.align}${c.sticky ? ' im-th--sticky' : ''}`}
                      style={stickyStyle}
                      onClick={() => onSort(c.key)}
                    >
                      <span className="im-th__inner">
                        <span>{c.label}</span>
                        <span className={`im-sort ${isSort ? `im-sort--${sort.dir}` : ''}`}>
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                        </span>
                      </span>
                    </th>
                  );
                })}
                <th className="im-th im-th--actions" style={{ right: 0 }}></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={orderedCols.length + 2}>
                  <div className="mi-empty"><div className="mi-empty__title">Caricamento…</div></div>
                </td></tr>
              )}
              {error && !loading && (
                <tr><td colSpan={orderedCols.length + 2}>
                  <div className="mi-empty">
                    <div className="mi-empty__title">Errore</div>
                    <div className="mi-empty__body">{error}</div>
                  </div>
                </td></tr>
              )}
              {!loading && !error && rows.map((row) => (
                <tr key={row.id} className="im-tr" data-pref={row.preferito ? 'true' : 'false'}>
                  <td className="im-td im-td--check" style={{ left: 0 }}>
                    <label className="mi-checkbox">
                      <input type="checkbox" aria-label={`Seleziona ${row.indirizzo}`} />
                      <span className="mi-checkbox__box"></span>
                    </label>
                  </td>
                  {orderedCols.map((c) => {
                    const stickyStyle = c.sticky
                      ? { left: stickyOffsets[c.key], width: c.width, minWidth: c.width }
                      : { width: c.width, minWidth: c.width };
                    return (
                      <td
                        key={c.key}
                        className={`im-td im-td--${c.align}${c.sticky ? ' im-td--sticky' : ''}`}
                        style={stickyStyle}
                      >
                        {cellContent(c, row)}
                      </td>
                    );
                  })}
                  <td className="im-td im-td--actions" style={{ right: 0 }}>
                    <RowActionsMenu
                      row={row}
                      onTogglePreferito={handleTogglePreferito}
                      onToggleScarta={handleToggleScarta}
                      onStartAstemaster={handleStartAstemaster}
                      onPublishGetrix={handlePublishGetrix}
                      onOpenOmi={setOmiRow}
                      publishing={publishingId === row.id}
                    />
                  </td>
                </tr>
              ))}
              {!loading && !error && rows.length === 0 && (
                <tr>
                  <td colSpan={orderedCols.length + 2}>
                    <div className="mi-empty">
                      <div className="mi-empty__title">Nessun immobile corrisponde ai filtri</div>
                      <div className="mi-empty__body">Prova a cambiare vista, verdetto o pulire la ricerca.</div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mi-footer">
        <div className="mi-footer__info">
          {meta?.total != null ? (
            <>
              {((meta.current_page - 1) * meta.per_page + 1).toLocaleString('it-IT')}–
              {Math.min(meta.current_page * meta.per_page, meta.total).toLocaleString('it-IT')}
              {' di '}{meta.total.toLocaleString('it-IT')} immobili
            </>
          ) : (
            `${rows.length} immobili`
          )}
          {' · '}{orderedCols.length} colonne visibili
        </div>
        {meta?.last_page > 1 && (
          <Pagination
            current={meta.current_page}
            last={meta.last_page}
            onChange={goToPage}
          />
        )}
      </div>

      <FilterPanel
        open={filterPanelOpen}
        onClose={() => setFilterPanelOpen(false)}
        searchParams={searchParams}
        setSearchParams={setSearchParams}
        filterOptions={filterOptions}
      />
      {omiRow && (
        <OmiReportModal row={omiRow} onClose={() => setOmiRow(null)} />
      )}
    </main>
  );
}

export default ImmobiliPage;
